import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { GraduationCap, Users } from 'lucide-react'

import { instrumentsApi, locationsApi, teachersApi } from '@/api/endpoints'
import { useAuth } from '@/auth/AuthContext'
import { isTeacher } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { LocationBadges } from '@/components/ui/LocationBadges'
import { InstrumentBadges } from '@/components/ui/InstrumentBadge'
import { Pagination } from '@/components/ui/Pagination'
import { SkeletonRows } from '@/components/ui/Spinner'
import {
  DataTable,
  EmptyRow,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/Table'
import { ToggleChip, Toolbar } from '@/components/ui/Toolbar'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { plural } from '@/lib/format'

const PAGE_SIZE = 20

const FILTER_SELECT =
  'cursor-pointer rounded-full border-2 border-transparent bg-surface-container-low px-4 py-2.5 text-sm font-semibold text-on-surface-variant transition-colors focus:border-primary focus:bg-surface focus:outline-none'

export function TeachersPage() {
  const { user } = useAuth()
  const showRates = !isTeacher(user)

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [instrumentId, setInstrumentId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [acceptingOnly, setAcceptingOnly] = useState(false)

  const debouncedSearch = useDebouncedValue(search)

  const query = useQuery({
    queryKey: [
      'teachers',
      { page, search: debouncedSearch, instrumentId, locationId, acceptingOnly },
    ],
    queryFn: () =>
      teachersApi.list({
        page,
        size: PAGE_SIZE,
        search: debouncedSearch || undefined,
        instrument_id: instrumentId || undefined,
        location_id: locationId || undefined,
        accepting_only: acceptingOnly,
      }),
    placeholderData: keepPreviousData,
  })

  const instrumentsQuery = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentsApi.list(),
  })
  const locationsQuery = useQuery({
    queryKey: ['locations', 'picker'],
    queryFn: () => locationsApi.list({ size: 200 }),
  })

  const items = query.data?.items ?? []
  const isEmpty = !query.isPending && items.length === 0
  const hasFilters = search !== '' || instrumentId !== '' || locationId !== '' || acceptingOnly

  return (
    <>
      <PageHeader
        title="Nauczyciele"
        description="Specjalizacje, dostępność tygodniowa i liczba podopiecznych."
        breadcrumb={['Szkoła', 'Nauczyciele']}
      />

      <Toolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder="Szukaj po imieniu, nazwisku lub e-mailu…"
      >
        <select
          value={instrumentId}
          onChange={(event) => {
            setInstrumentId(event.target.value)
            setPage(1)
          }}
          aria-label="Filtr instrumentu"
          className={FILTER_SELECT}
        >
          <option value="">Wszystkie instrumenty</option>
          {(instrumentsQuery.data ?? []).map((instrument) => (
            <option key={instrument.id} value={instrument.id}>
              {instrument.name}
            </option>
          ))}
        </select>

        <select
          value={locationId}
          onChange={(event) => {
            setLocationId(event.target.value)
            setPage(1)
          }}
          aria-label="Filtr oddziału"
          className={FILTER_SELECT}
        >
          <option value="">Wszystkie oddziały</option>
          {(locationsQuery.data?.items ?? []).map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>

        <ToggleChip
          checked={acceptingOnly}
          onChange={(checked) => {
            setAcceptingOnly(checked)
            setPage(1)
          }}
          label="Tylko przyjmujący uczniów"
        />
      </Toolbar>

      {query.isError && (
        <Alert tone="error" className="mb-6">
          Nie udało się wczytać listy nauczycieli. Odśwież stronę i spróbuj ponownie.
        </Alert>
      )}

      <Card>
        {query.isPending ? (
          <SkeletonRows />
        ) : isEmpty ? (
          <EmptyState
            icon={<GraduationCap size={28} />}
            title={hasFilters ? 'Brak wyników' : 'Nie ma jeszcze żadnego nauczyciela'}
            description={
              hasFilters
                ? 'Zmień kryteria wyszukiwania albo wyczyść filtry.'
                : 'Konta nauczycieli zakłada się w sekcji „Konta”.'
            }
          />
        ) : (
          <>
            <DataTable caption="Lista nauczycieli">
              <TableHead
                columns={[
                  'Nauczyciel',
                  'Instrumenty',
                  'Oddziały',
                  'Uczniowie',
                  ...(showRates ? ['Stawka'] : []),
                  'Status',
                ]}
              />
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.user.id}>
                    <TableCell>
                      <Link
                        to={`/nauczyciele/${item.user.id}`}
                        className="flex items-center gap-3 rounded-lg transition-opacity hover:opacity-75"
                      >
                        <Avatar name={item.user.full_name} />
                        <div>
                          <p className="font-bold text-on-surface">{item.user.full_name}</p>
                          {/* <p className="text-xs text-primary">Zobacz profil</p> */}
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>
                      {item.instruments.length === 0 ? (
                        <span className="text-xs text-on-surface-variant">Nie uzupełniono</span>
                      ) : (
                        <InstrumentBadges instruments={item.instruments} />
                      )}
                    </TableCell>
                    <TableCell>
                      <LocationBadges locations={item.locations} hasAll={item.has_all_locations} />
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 text-sm">
                        <Users size={14} aria-hidden="true" />
                        {item.student_count}{' '}
                        <span className="text-on-surface-variant">
                          {plural(item.student_count, 'uczeń', 'uczniów', 'uczniów')}
                        </span>
                      </span>
                    </TableCell>
                    {showRates && (
                      <TableCell>
                        {item.hourly_rate ? (
                          <span className="text-sm font-semibold">{item.hourly_rate} zł/h</span>
                        ) : (
                          <span className="text-xs text-on-surface-variant">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge tone={item.is_accepting_students ? 'success' : 'neutral'}>
                        {item.is_accepting_students ? 'Przyjmuje' : 'Komplet'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <EmptyRow colSpan={showRates ? 6 : 5} message="Brak nauczycieli." />
                )}
              </TableBody>
            </DataTable>

            <Pagination
              page={page}
              size={PAGE_SIZE}
              total={query.data?.total ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>
    </>
  )
}
